import { Api, Currency, CurrencySettings, LogManager, ModelRow } from "@mcleod/core";
import { ReportData } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { BackOfficeReportPreview } from "./BackOfficeReportPreview";
import { OrderPayablesProcessStepperProps } from "./OrderPayablesProcessStepper";
import { AutogenLayoutOrderPayablesProcessPostPayables } from "./autogen/AutogenLayoutOrderPayablesProcessPostPayables";

const log = LogManager.getLogger("lme.settlement.OrderPayablesProcessSummary");

const PAYMENT_METHOD_CHECK = "C";
const PAYMENT_METHOD_DIRECT_DEPOSIT = "D";
const PAYMENT_METHOD_FACTORING = "5";

export class OrderPayablesProcessPostPayables extends AutogenLayoutOrderPayablesProcessPostPayables {
    private _reportPreviewPostPayroll: BackOfficeReportPreview;
    public markStepCompleted: () => void;

    get reportPreviewPostPayroll(): BackOfficeReportPreview {
        this.initializeReportPreviewPostPayroll();
        return this._reportPreviewPostPayroll;
    }

    private set reportPreviewPostPayroll(value: BackOfficeReportPreview) {
        this._reportPreviewPostPayroll = value;
    }

    private initializeReportPreviewPostPayroll() {
        if (this._reportPreviewPostPayroll == null) {
            this.reportPreviewPostPayroll = new BackOfficeReportPreview("", false);
            this.add(this.reportPreviewPostPayroll);
        }
    }

    public async postPayroll(props: Partial<OrderPayablesProcessStepperProps>): Promise<any> {
        return new Promise((resolve, reject) => {
            const endpointPath = props.isOffice ? "lme/backoffice/officepayroll/post-payroll" : "lme/settlement/order-payables-post-payroll";
            Api.post(endpointPath,
                {
                    payee_ids: props.payeeIds,
                    payee_types: props.payeeTypes,
                    settlement_ids: props.settlementIds,
                    deduction_ids: props.deductionIds,
                    start_date: props.startDate,
                    end_date: props.endDate,
                    check_date: props.checkDate,
                    cutoff_date: props.cutoffDate,
                    recurring_date: props.recurringDate,
                    bonus_code: props.profileRow.get("bonus_code"),
                    currency_code: props.profileRow.get("currency_type"),
                    override_frequency: props.profileRow.get("override_frequency"),
                    bonus_tax_method: props.profileRow.get("bonus_tax_method"),
                    is_office: props.isOffice
                })
                .then(response => {
                    const reports = [];
                    response?.data[0]?.post_payroll_report_data?.forEach((reportData: ReportData) => {
                        reports.push(reportData);
                    });
                    this.markStepCompleted?.();
                    props.processLog?.refreshLastProcessedData();
                    resolve(reports);
                }).catch(error => {
                    CommonDialogs.showError(error, "Error posting payroll");
                    resolve([]);
                });
        });
    }

    public async setToPrePostView(printedChecks: ModelRow[], props: Partial<OrderPayablesProcessStepperProps>) {
        let checkCountTotal = 0;
        let checkDirectDepositCountTotal = 0;
        let checkOtherCountTotal = 0;
        const checkAmountTotal = new Currency(0);
        const checkDirectDepositAmountTotal = new Currency(0);
        const checkOtherAmountTotal = new Currency(0);
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            checkAmountTotal.setCurrencyCode(props.profileRow.get("currency_type", "USD"));
            checkDirectDepositAmountTotal.setCurrencyCode(props.profileRow.get("currency_type", "USD"));
            checkOtherAmountTotal.setCurrencyCode(props.profileRow.get("currency_type", "USD"));
        }

        if (printedChecks.length != 0) {
            for (const check of printedChecks) {
                if (PAYMENT_METHOD_FACTORING === check.get("payment_method") && PAYMENT_METHOD_CHECK === check.get("factoring_payment_method")) {
                    checkCountTotal++;
                    await checkAmountTotal.add(check.getCurrency("amount"));
                } else if (PAYMENT_METHOD_FACTORING === check.get("payment_method") && PAYMENT_METHOD_DIRECT_DEPOSIT === check.get("factoring_payment_method")) {
                    checkDirectDepositCountTotal++;
                    await checkDirectDepositAmountTotal.add(check.getCurrency("amount"));
                } else if (PAYMENT_METHOD_CHECK === check.get("payment_method")) {
                    checkCountTotal++;
                    await checkAmountTotal.add(check.getCurrency("amount"));
                } else if (PAYMENT_METHOD_DIRECT_DEPOSIT === check.get("payment_method")) {
                    checkDirectDepositCountTotal++;
                    await checkDirectDepositAmountTotal.add(check.getCurrency("amount"));
                } else {
                    checkOtherCountTotal++;
                    await checkOtherAmountTotal.add(check.getCurrency("amount"));
                }
            }
        }

        this.textboxCheckCount.caption = "Checks (" + checkCountTotal + ")";
        this.textboxCheckCount.text = checkAmountTotal.toString();
        this.textboxDirectDepositCount.caption = "Direct Deposit (" + checkDirectDepositCountTotal + ")";
        this.textboxDirectDepositCount.text = checkDirectDepositAmountTotal.toString();
        this.textboxOtherTypesCount.caption = "Other Payment Types (" + checkOtherCountTotal + ")";
        this.textboxOtherTypesCount.text = checkOtherAmountTotal.toString();

        this.textboxTotalCount.caption = "Total (" + (checkCountTotal + checkDirectDepositCountTotal + checkOtherCountTotal) + ")";
        this.textboxTotalCount.text = new Currency({
            amount: checkAmountTotal.amount + checkDirectDepositAmountTotal.amount + checkOtherAmountTotal.amount,
            currency_code: props.profileRow.get("currency_type", "USD")
        }).toString();
    }

    public setToPostedView() {
        this.labelPostConfirmation.rowBreak = false;
        this.labelPostConfirmation.text = "Post Confirmation";
        this.panelCheckCount.visible = false;
        this.panelDirectDepositCount.visible = false;
        this.panelOtherTypesCount.visible = false;
        this.panelTotalCount.visible = false;
        this.buttonPostPayables.visible = false;
    }
}
